import { nodeToFlareXML } from '@common/flare/util/prosemirror-flare-xml';
import { nbsp } from '@common/html/util/dom';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { getSchemaAttrs, hasSchemaAttr, nodeToDOM } from '@common/util/schema';
import { NodeSpec } from 'prosemirror-model';

export type LegacySchemaNodes = {
  /**
   * @deprecated
   * <comment> is deprecated. It existed in the schema in the past but is neither an HTML tag or a Flare tag.
   */
  comment: NodeSpec;
  /**
   * @deprecated
   * <madcapbookmark> is deprecated. The bookmark logic has been moved to the link <a> tag schema since a bookmark is just a link with a name attribute.
   */
  madcapbookmark: NodeSpec;
  /**
   * @deprecated
   * <madcapCentralNbspBlock> is deprecated. It existed in the schema in the past but is not needed with SimpleDOM.
   */
  madcapcentralnbspblock: NodeSpec;
  /**
   * @deprecated
   * <madcapcentralnbspinline> is deprecated. It existed in the schema in the past but is not needed with SimpleDOM.
   */
  madcapcentralnbspinline: NodeSpec;
  /**
   * @deprecated
   * <node> is deprecated. It existed in the schema in the past but is neither an HTML tag or a Flare tag.
   */
  node: NodeSpec;
};

/**
 * Schema plugin to support legacy tags that should no longer be used but may exist in older documents.
 * These tags must remain in the schema to avoid throwing excpeptions when loading older documents that contain them.
 */
export class LegacySchemaPlugin extends SchemaPlugin {
  nodes = {
    /** <comment> is deprecated. It existed in the schema in the past but is neither an HTML tag or a Flare tag. */
    comment: {
      group: 'block',
      content: 'inline*',
      parseDOM: [{
        tag: 'comment'
      }],
      toDOM(node) { return nodeToDOM(node); }
    },

    /** <madcapbookmark> is deprecated. The bookmark logic has been moved to the link <a> tag schema since a bookmark is just a link with a name attribute. */
    madcapbookmark: {
      supersededBy: 'link',
      group: 'inline',
      linkBucket: 'bookmark',
      inline: true,
      content: 'inline*',
      definingForContent: true,
      attrs: {
        name: {
          default: undefined,
          specRequired: true,
          specValue: 'test'
        }
      },
      parseDOM: [{
        tag: 'a[name]',
        // The priority must be lower than a link so that this never matches.
        priority: 0,
        getAttrs(dom: HTMLElement) {
          if (hasSchemaAttr(dom, 'href')) {
            return false;
          }

          return getSchemaAttrs(dom, ['name']);
        }
      }],
      toDOM(node) { return nodeToDOM(node); },
      tagName: 'a',
      toDisplayName() { return 'MadCap:bookmark'; }
    },

    /** <madcapCentralNbspBlock> is deprecated. It existed in the schema in the past but is not needed with SimpleDOM. */
    madcapcentralnbspblock: {
      group: 'block',
      inlineAndBlock: true,
      unchangeable: true, // Tracked changes cannot be put on this node forcing a tracked change mark to be placed around the node instead
      parseDOM: [{
        tag: 'MadCap\\:centralNbsp'
      }, {
        tag: 'span',
        getAttrs(dom: HTMLElement) {
          if (!dom.classList.contains('mc-madcap-central-nbsp')) {
            return false;
          }
        }
      }],
      toDOM(node) {
        const outputSpec = nodeToDOM(node, { isVoid: true, tagName: 'mc-lwe-nbsp' });
        outputSpec.dom.textContent = nbsp;
        return outputSpec;
      },
      toFlareXML(node, options): string {
        return nodeToFlareXML(node.type.schema.text(nbsp, node.marks), options, null);
      },
      toDisplayName() { return 'nbsp'; },
      specSkip: true
    },

    /** <madcapcentralnbspinline> is deprecated. It existed in the schema in the past but is not needed with SimpleDOM. */
    madcapcentralnbspinline: {
      group: 'inline',
      inline: true,
      inlineAndBlock: true,
      atom: true,
      unchangeable: true, // Tracked changes cannot be put on this node forcing a tracked change mark to be placed around the node instead
      parseDOM: [{
        tag: 'MadCap\\:centralNbsp',
        context: 'block/|inline/',
        // This should never match
        priority: 0
      }, {
        tag: 'span',
        context: 'block/|inline/',
        // This should never match
        priority: 0,
        getAttrs(dom) {
          if (!dom.classList.contains('mc-madcap-central-nbsp')) {
            return false;
          }
        }
      }],
      toDOM(node) {
        const outputSpec = nodeToDOM(node, { isVoid: true, tagName: 'mc-lwe-nbsp' });
        outputSpec.dom.textContent = nbsp;
        return outputSpec;
      },
      toFlareXML(node, options): string {
        return nodeToFlareXML(node.type.schema.text(nbsp, node.marks), options, null);
      },
      toDisplayName() { return 'nbsp'; },
      specSkip: true
    },

    /** <node> is deprecated. It existed in the schema in the past but is neither an HTML tag or a Flare tag. */
    node: {
      group: 'inline',
      content: 'inline*',
      inline: true,
      definingForContent: true,
      parseDOM: [{
        tag: 'node',
        preserveWhitespace: true
      }],
      toDOM(node) { return nodeToDOM(node); }
    }
  } satisfies LegacySchemaNodes;
}
